import { useMemo } from 'react'
import cx from 'classnames'
import { observer } from 'mobx-react-lite'

import InfoIcon from 'almanac-ui/icons/16/Info'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { useUpdateLayerSharingMode } from 'hooks/useUpdateLayerSharingMode'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useSuperAdmin } from 'hooks/useSuperAdmin'
import {
  FileAccessGrant,
  FileAccessGrantAccessor,
  RecipientsSelectorValueType,
  AssetAccessGrantWithParent,
} from 'types'
import useShareSheetV2 from 'hooks/useShareSheetV2'
import { Value as RecipientsSelectorValue } from 'components/ShareSheet/RecipientSelector/Types'

import { orderAccesses } from './utils'
import { ShareSheetProps } from './Types'
import { AccessRow } from './Access/AccessRow'
import Styles from './ShareSheet.module.scss'
import { Heading } from 'blaze-ui/Heading/Heading'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import { Text } from 'blaze-ui/Text/Text'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'

const layerEditorAccess = (mainLayerEditors: FileAccessGrantAccessor[]) => {
  return {
    id: -1,
    access: 'edit',
    access_number: 30,
    other_access: false,
    asset: { id: 'layerEditors', url: '', header_emoji: null, title: '' } as AssetAccessGrantWithParent,
    main_layer_editors: mainLayerEditors,
    accessor: {
      id: -1,
      type: RecipientsSelectorValueType.LayerEditors,
      name: 'Members who can edit main doc',
      email: '',
      avatar_url: '',
      member_count: mainLayerEditors.length,
    },
  } as FileAccessGrant
}

export const Accesses = observer(({ file }: ShareSheetProps) => {
  const {
    asset,
    invitedAccesses,
    userCanEdit,
    inheritedAccesses,
    onRemoveAccess,
    onUpdateAccess,
    setShareSheet,
    publicAccess,
    footerOpen,
    mainLayerEditors,
    loadShareSheet,
  } = useShareSheetV2(file)
  const isSystemRedesignEnabled = useIsSystemRedesign()
  const updateSharingMode = useUpdateLayerSharingMode({ layerId: asset?.id || '', articleId: asset?.parent?.id || '' })
  const { isMobile } = useResponsiveDesign()
  const { superAdminMode } = useSuperAdmin()
  const orderedInheritedAccesses = useMemo(() => {
    return orderAccesses(inheritedAccesses)
  }, [inheritedAccesses])

  const orderedInvitedAccesses = useMemo(() => {
    const accessesToSort =
      asset?.sharing_mode === 'public' ? [...invitedAccesses, layerEditorAccess(mainLayerEditors)] : invitedAccesses

    return orderAccesses(accessesToSort)
  }, [invitedAccesses, asset, mainLayerEditors])

  const onTransfer = ({ access }: { access: FileAccessGrant }) => {
    const {
      accessor: { id, name, avatar_url },
    } = access

    setShareSheet({ transferOwnershipUser: { id, name, avatar_url } })
  }

  const onTakeOwnership = () => {
    setShareSheet({ takeOwnership: true })
  }

  const onInvite = (recipient: RecipientsSelectorValue) => {
    setShareSheet({ recipients: [recipient] })
  }

  const updateLayerArticleEditors = () => {
    updateSharingMode.makeShared()
    loadShareSheet({})
  }

  return (
    <div className={cx(Styles.accesses, { [Styles.withPublicAccess]: publicAccess && footerOpen })}>
      <div>
        <div className={Styles.header}>
          <ResponsiveDesign.Mobile>
            <Text variant="smallList" color="var(--dark-80)">
              Invited
            </Text>
          </ResponsiveDesign.Mobile>
          <ResponsiveDesign.Desktop>
            <Heading level={5}>Invited</Heading>
          </ResponsiveDesign.Desktop>
        </div>
        <div className={Styles.accessList}>
          {orderedInvitedAccesses.map((access) => {
            if (access.accessor.type === RecipientsSelectorValueType.LayerEditors) {
              return (
                <AccessRow
                  childMembers={mainLayerEditors}
                  key={access.id}
                  access={access}
                  asset={asset || null}
                  invited
                  superAdminMode={superAdminMode}
                  onRemoveAccess={() => updateLayerArticleEditors()}
                  userCanEdit={userCanEdit}
                />
              )
            }

            return (
              <AccessRow
                key={access.id}
                access={access}
                childMembers={access.accessor.workspace_members || []}
                invited
                userCanEdit={userCanEdit}
                onInvite={onInvite}
                onRemoveAccess={onRemoveAccess}
                onUpdateAccess={onUpdateAccess}
                superAdminMode={superAdminMode}
                asset={asset || null}
                onTransfer={onTransfer}
                onTakeOwnership={onTakeOwnership}
              />
            )
          })}
        </div>
      </div>
      {!!inheritedAccesses.length && (
        <div>
          <div className={Styles.header}>
            <Heading level={isSystemRedesignEnabled ? 5 : 4}>Inherited Access</Heading>
            {!isMobile && (
              <Tooltip
                label={`Members with access to this ${
                  asset?.type || 'document'
                } (because they have access to a parent folder)`}
              >
                <div className={Styles.icon}>
                  <InfoIcon color="#8F98A9" />
                </div>
              </Tooltip>
            )}
          </div>
          <div className={Styles.accessList}>
            {orderedInheritedAccesses.map((access) => {
              return (
                <AccessRow
                  key={access.id}
                  access={access}
                  invited={false}
                  userCanEdit={false}
                  onInvite={onInvite}
                  superAdminMode={superAdminMode}
                  childMembers={access.accessor.workspace_members || []}
                  onRemoveAccess={onRemoveAccess}
                  onUpdateAccess={onUpdateAccess}
                  asset={asset || null}
                  onTransfer={onTransfer}
                  onTakeOwnership={onTakeOwnership}
                />
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
})
